<template>
  <side-modal
    :value="value"
    :eyebrow="$t('hedgeOS.dashboard.ticketDrawer.eyebrow')"
    :title="$t('hedgeOS.dashboard.ticketDrawer.title')"
    :description="$t('hedgeOS.dashboard.ticketDrawer.description')"
    width="720px"
    :footer-meta="$t('hedgeOS.dashboard.ticketDrawer.footerMeta')"
    :secondary-label="$t('hedgeOS.dashboard.ticketDrawer.cancel')"
    :primary-label="$t('hedgeOS.dashboard.ticketDrawer.execute')"
    class="hedge-ticket-drawer"
    @input="$emit('input', $event)"
    @secondary="$emit('input', false)"
    @primary="$emit('primary')"
  >
    <div class="ticket-summary">
      <div class="lbl">{{ $t('hedgeOS.dashboard.ticketDrawer.summary.label') }}</div>
      <div class="op-line">
        <span class="id">EXP-0142</span>
        <span>{{ $t('hedgeOS.dashboard.ticketDrawer.summary.operationName') }}</span>
      </div>
      <div class="op-meta">
        {{ $t('hedgeOS.dashboard.ticketDrawer.summary.meta') }}<br />
        {{ $t('hedgeOS.dashboard.ticketDrawer.summary.notionalLine') }}<br />
        {{ $t('hedgeOS.dashboard.ticketDrawer.summary.fxLine') }}
      </div>
      <div class="gap-callout">
        {{ $t('hedgeOS.dashboard.ticketDrawer.summary.gapLabel') }}
        <span class="gap-val">{{ $t('hedgeOS.dashboard.ticketDrawer.summary.gapValue') }}</span>
      </div>
    </div>

    <form @submit.prevent>
      <div class="form-section">
        <h4>{{ $t('hedgeOS.dashboard.ticketDrawer.sections.tradeDefinition') }}</h4>
        <div class="form-grid">
          <div class="field">
            <label>{{ $t('hedgeOS.dashboard.ticketDrawer.fields.side') }} <span class="req">*</span></label>
            <div class="seg">
              <input id="ticket-side-buy" v-model="side" type="radio" value="buy" />
              <label for="ticket-side-buy">{{ $t('hedgeOS.dashboard.ticketDrawer.fields.buyEur') }}</label>
              <input id="ticket-side-sell" v-model="side" type="radio" value="sell" />
              <label for="ticket-side-sell">{{ $t('hedgeOS.dashboard.ticketDrawer.fields.sellEur') }}</label>
            </div>
          </div>
          <div class="field">
            <label>{{ $t('hedgeOS.dashboard.ticketDrawer.fields.pair') }}</label>
            <input type="text" value="EUR/BRL" readonly />
          </div>
          <div class="field">
            <label>{{ $t('hedgeOS.dashboard.ticketDrawer.fields.notional') }} <span class="hint">{{ $t('hedgeOS.dashboard.ticketDrawer.fields.notionalHint') }}</span></label>
            <input v-model.number="notional" type="number" step="10000" />
          </div>
          <div class="field">
            <label>{{ $t('hedgeOS.dashboard.ticketDrawer.fields.coverage') }} <span class="hint">{{ $t('hedgeOS.dashboard.ticketDrawer.fields.auto') }}</span></label>
            <input type="text" :value="coverageLabel" readonly />
          </div>
        </div>
      </div>

      <div class="form-section">
        <h4>{{ $t('hedgeOS.dashboard.ticketDrawer.sections.tenorRate') }}</h4>
        <div class="form-grid">
          <div class="field">
            <label>{{ $t('hedgeOS.dashboard.ticketDrawer.fields.maturity') }} <span class="hint">{{ $t('hedgeOS.dashboard.ticketDrawer.fields.maturityHint') }}</span></label>
            <input type="date" value="2026-06-30" readonly />
          </div>
          <div class="field">
            <label>{{ $t('hedgeOS.dashboard.ticketDrawer.fields.tenor') }}</label>
            <input type="text" :value="$t('hedgeOS.dashboard.ticketDrawer.fields.tenorValue')" readonly />
          </div>
          <div class="field">
            <label>{{ $t('hedgeOS.dashboard.ticketDrawer.fields.forwardRate') }} <span class="req">*</span></label>
            <input v-model.number="forwardRate" type="number" step="0.0001" />
          </div>
          <div class="field">
            <label>{{ $t('hedgeOS.dashboard.ticketDrawer.fields.spotNow') }} <span class="hint">{{ $t('hedgeOS.dashboard.ticketDrawer.fields.live') }}</span></label>
            <input type="text" value="6.3120" readonly />
          </div>
        </div>

        <div class="curve-preview">
          <h5>
            {{ $t('hedgeOS.dashboard.ticketDrawer.curve.title') }}
            <span class="now">67d · {{ forwardRate.toFixed(4) }}</span>
          </h5>
          <svg viewBox="0 0 600 100" preserveAspectRatio="none">
            <line class="grid-line" x1="0" y1="30" x2="600" y2="30" />
            <line class="grid-line" x1="0" y1="60" x2="600" y2="60" />
            <path
              fill="none"
              stroke="var(--cool)"
              stroke-width="1.8"
              d="M0,40 L60,42 L120,46 L180,49 L240,53 L300,58 L360,63 L420,68 L480,73 L540,78 L600,82"
            />
            <line x1="220" y1="0" x2="220" y2="95" stroke="var(--hot)" stroke-width="1" stroke-dasharray="3,3" />
            <circle cx="220" cy="51" r="4" fill="var(--hot)" />
            <text x="226" y="48" class="axis-label hot">67d · {{ forwardRate.toFixed(4) }}</text>
            <text x="0" y="95" class="axis-label">{{ $t('hedgeOS.dashboard.ticketDrawer.curve.spot') }}</text>
            <text x="160" y="95" class="axis-label">{{ $t('hedgeOS.dashboard.ticketDrawer.curve.d90') }}</text>
            <text x="320" y="95" class="axis-label">{{ $t('hedgeOS.dashboard.ticketDrawer.curve.d180') }}</text>
            <text x="490" y="95" class="axis-label">{{ $t('hedgeOS.dashboard.ticketDrawer.curve.d360') }}</text>
          </svg>
        </div>
      </div>

      <div class="form-section">
        <h4>{{ $t('hedgeOS.dashboard.ticketDrawer.sections.counterpartySettlement') }}</h4>
        <div class="form-grid">
          <div class="field">
            <label>{{ $t('hedgeOS.dashboard.ticketDrawer.fields.counterpartyBank') }} <span class="req">*</span></label>
            <select v-model="bank">
              <option v-for="quote in bankQuotes" :key="quote.name" :value="quote.name">
                {{ quote.name }} · {{ $t('hedgeOS.dashboard.ticketDrawer.fields.bestQuote') }} {{ quote.rate }}
              </option>
            </select>
          </div>
          <div class="field">
            <label>{{ $t('hedgeOS.dashboard.ticketDrawer.fields.creditLineUsed') }} <span class="hint">{{ $t('hedgeOS.dashboard.ticketDrawer.fields.postTrade') }}</span></label>
            <input type="text" value="42% → 51%" readonly />
          </div>
          <div class="field">
            <label>{{ $t('hedgeOS.dashboard.ticketDrawer.fields.settlementType') }}</label>
            <select v-model="settlementType">
              <option value="ndf">{{ $t('hedgeOS.dashboard.ticketDrawer.fields.ndfNonDeliverable') }}</option>
              <option value="deliverable">{{ $t('hedgeOS.dashboard.ticketDrawer.fields.deliverableForward') }}</option>
            </select>
          </div>
          <div class="field">
            <label>{{ $t('hedgeOS.dashboard.ticketDrawer.fields.referenceRate') }}</label>
            <select v-model="referenceRate">
              <option value="ptax">{{ $t('hedgeOS.dashboard.ticketDrawer.fields.ptaxFixing') }}</option>
              <option value="ebc">{{ $t('hedgeOS.dashboard.ticketDrawer.fields.ebcFixing') }}</option>
            </select>
          </div>
        </div>
      </div>

      <div class="form-section">
        <h4>{{ $t('hedgeOS.dashboard.ticketDrawer.sections.designation') }}</h4>
        <div class="field full">
          <label>{{ $t('hedgeOS.dashboard.ticketDrawer.fields.hedgeAccounting') }} <span class="hint">{{ $t('hedgeOS.dashboard.ticketDrawer.fields.hedgeAccountingHint') }}</span></label>
          <div class="seg">
            <input id="ticket-desig-cf" v-model="designation" type="radio" value="cash-flow" />
            <label for="ticket-desig-cf">{{ $t('hedgeOS.dashboard.ticketDrawer.fields.cashFlowHedge') }}</label>
            <input id="ticket-desig-fv" v-model="designation" type="radio" value="fair-value" />
            <label for="ticket-desig-fv">{{ $t('hedgeOS.dashboard.ticketDrawer.fields.fairValue') }}</label>
            <input id="ticket-desig-econ" v-model="designation" type="radio" value="economic" />
            <label for="ticket-desig-econ">{{ $t('hedgeOS.dashboard.ticketDrawer.fields.economicOnly') }}</label>
          </div>
        </div>
      </div>

      <div class="form-section">
        <h4>{{ $t('hedgeOS.dashboard.ticketDrawer.sections.economics') }}</h4>
        <div class="ticket-econ">
          <div class="row"><span class="k">{{ $t('hedgeOS.dashboard.ticketDrawer.economics.notional') }}</span><span class="v">EUR {{ notionalLabel }}</span></div>
          <div class="row"><span class="k">{{ $t('hedgeOS.dashboard.ticketDrawer.economics.forwardRate') }}</span><span class="v">{{ forwardRate.toFixed(4) }}</span></div>
          <div class="row"><span class="k">{{ $t('hedgeOS.dashboard.ticketDrawer.economics.spotReference') }}</span><span class="v">6.3120</span></div>
          <div class="row"><span class="k">{{ $t('hedgeOS.dashboard.ticketDrawer.economics.forwardPoints') }}</span><span class="v">{{ $t('hedgeOS.dashboard.ticketDrawer.economics.forwardPointsValue') }}</span></div>
          <div class="row"><span class="k">{{ $t('hedgeOS.dashboard.ticketDrawer.economics.impliedCarry') }}</span><span class="v">{{ $t('hedgeOS.dashboard.ticketDrawer.economics.impliedCarryValue') }}</span></div>
          <div class="row total">
            <span class="k">{{ $t('hedgeOS.dashboard.ticketDrawer.economics.lockedValue') }}</span>
            <span class="v">{{ lockedValueLabel }}</span>
          </div>
        </div>
      </div>
    </form>
  </side-modal>
</template>

<script>
import SideModal from "@/components/modal/SideModal.vue";

const OPERATION_NOTIONAL = 1400000;

export default {
  name: "HedgeTicketDrawer",
  components: { SideModal },
  props: {
    value: Boolean,
  },
  data() {
    return {
      side: "sell",
      notional: OPERATION_NOTIONAL,
      forwardRate: 6.294,
      bank: "Itaú BBA",
      bankQuotes: [
        { name: "Itaú BBA", rate: "6.2940" },
        { name: "Bradesco", rate: "6.2935" },
        { name: "Santander BR", rate: "6.2928" },
        { name: "Citi", rate: "6.2922" },
        { name: "BTG Pactual", rate: "6.2918" },
      ],
      settlementType: "ndf",
      referenceRate: "ptax",
      designation: "cash-flow",
    };
  },
  computed: {
    notionalLabel() {
      return (this.notional || 0).toLocaleString("en-US");
    },
    coverageLabel() {
      const pct = OPERATION_NOTIONAL ? Math.min(100, ((this.notional || 0) / OPERATION_NOTIONAL) * 100) : 0;
      return `${pct.toFixed(0)}%`;
    },
    lockedValueLabel() {
      const value = (this.notional || 0) * (this.forwardRate || 0);
      return `R$ ${value.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
    },
  },
};
</script>

<style scoped>
.ticket-summary {
  background: var(--ink2, #131615);
  border: 1px solid var(--line);
  padding: 1.3rem;
  margin-bottom: 1.5rem;
  border-left: 3px solid var(--hot);
}

.ticket-summary .lbl {
  font-family: "JetBrains Mono", monospace;
  font-size: 0.55rem;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.18em;
  margin-bottom: 0.4rem;
}

.ticket-summary .op-line {
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  font-family: "Fraunces", serif;
  font-size: 1.05rem;
  font-weight: 500;
  margin-bottom: 0.6rem;
  color: var(--paper);
}

.ticket-summary .op-line .id {
  font-family: "JetBrains Mono", monospace;
  font-size: 0.7rem;
  color: var(--hot);
  font-weight: 400;
}

.ticket-summary .op-meta {
  font-family: "JetBrains Mono", monospace;
  font-size: 0.7rem;
  color: var(--dim);
  line-height: 1.6;
}

.ticket-summary .gap-callout {
  margin-top: 0.8rem;
  padding-top: 0.8rem;
  border-top: 1px solid var(--line);
  font-family: "JetBrains Mono", monospace;
  font-size: 0.72rem;
  color: var(--muted);
}

.ticket-summary .gap-callout .gap-val {
  font-family: "Fraunces", serif;
  font-size: 1rem;
  font-weight: 600;
  color: var(--hot);
  margin-left: 0.5rem;
}

.form-section {
  margin-bottom: 2rem;
}

.form-section + .form-section {
  border-top: 1px solid var(--line);
  padding-top: 1.6rem;
}

.form-section h4 {
  font-family: "JetBrains Mono", monospace;
  font-size: 0.62rem;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.18em;
  margin-bottom: 1rem;
  font-weight: 400;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.1rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.field.full {
  grid-column: 1 / -1;
}

.field label {
  font-family: "JetBrains Mono", monospace;
  font-size: 0.6rem;
  color: var(--dim);
  text-transform: uppercase;
  letter-spacing: 0.15em;
  display: flex;
  justify-content: space-between;
}

.field label .req {
  color: var(--hot);
  font-size: 0.7rem;
}

.field label .hint {
  color: var(--muted);
  font-size: 0.55rem;
  text-transform: none;
  letter-spacing: 0.05em;
}

.field input[type="text"],
.field input[type="number"],
.field input[type="date"],
.field select {
  background: var(--ink2, #131615);
  border: 1px solid var(--line);
  color: var(--paper);
  padding: 0.7rem 0.8rem;
  font-family: "JetBrains Mono", monospace;
  font-size: 0.85rem;
  transition: border-color 0.2s;
  width: 100%;
}

.field input:focus,
.field select:focus {
  outline: none;
  border-color: var(--hot);
}

.field input[readonly] {
  background: var(--ink3, #1c201e);
  color: var(--dim);
  cursor: not-allowed;
}

/* Segmented toggle control */
.seg {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  border: 1px solid var(--line);
  background: var(--ink2, #131615);
}

.seg input {
  display: none;
}

.seg label {
  cursor: pointer;
  padding: 0.7rem 0.5rem;
  text-align: center;
  font-family: "JetBrains Mono", monospace;
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--dim);
  border-right: 1px solid var(--line);
  transition: all 0.2s;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.seg label:last-child {
  border-right: none;
}

.seg label:hover {
  background: var(--ink3, #1c201e);
  color: var(--paper);
}

.seg input:checked + label {
  background: var(--hot);
  color: var(--ink);
  font-weight: 600;
}

.curve-preview {
  background: var(--ink2, #131615);
  border: 1px solid var(--line);
  padding: 1rem;
  margin-top: 1rem;
}

.curve-preview h5 {
  font-family: "JetBrains Mono", monospace;
  font-size: 0.6rem;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.15em;
  font-weight: 400;
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.7rem;
}

.curve-preview h5 .now {
  color: var(--paper);
  font-size: 0.78rem;
  font-weight: 500;
}

.curve-preview svg {
  width: 100%;
  height: 80px;
}

.grid-line {
  stroke: var(--grid, rgba(243, 239, 230, 0.06));
  stroke-width: 1;
}

.axis-label {
  font-family: "JetBrains Mono", monospace;
  font-size: 9px;
  fill: var(--muted);
}

.axis-label.hot {
  fill: var(--hot);
}

.ticket-econ {
  background: var(--ink2, #131615);
  border: 1px solid var(--line);
  padding: 1.1rem 1.3rem;
  margin-top: 0.5rem;
}

.ticket-econ .row {
  display: flex;
  justify-content: space-between;
  padding: 0.45rem 0;
  font-family: "JetBrains Mono", monospace;
  font-size: 0.75rem;
}

.ticket-econ .row .k {
  color: var(--dim);
}

.ticket-econ .row .v {
  color: var(--paper);
  font-weight: 500;
}

.ticket-econ .row.total {
  border-top: 1px solid var(--line);
  margin-top: 0.5rem;
  padding-top: 0.8rem;
}

.ticket-econ .row.total .k {
  font-family: "Fraunces", serif;
  font-size: 0.85rem;
  color: var(--paper);
  font-weight: 600;
}

.ticket-econ .row.total .v {
  font-family: "Fraunces", serif;
  font-size: 1.1rem;
  color: var(--hot);
  font-weight: 600;
}

@media (max-width: 700px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
